import React, { type PropsWithChildren, useCallback } from 'react';

import { FormattedMessage, type MessageDescriptor } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap, cx } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import { Label } from '@atlaskit/form/label/default';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { Box, Flex, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { useIsDisabledContext } from '../../../../controllers/disabled-context';
import type { RenderVariant } from '../../../../types';

import { OptionLabel } from './option-label';

const styles = cssMap({
	dropdownTrigger: {
		color: token('color.text.subtle'),
		paddingInline: token('space.150'),
		paddingBlock: token('space.075'),
		backgroundColor: token('color.background.neutral.subtle'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	dropdownFullWidthStyles: {
		width: '100%',
	},
	border: {
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
	},
	iconBackgroundStyles: {
		backgroundColor: token('color.background.accent.gray.subtler'),
		width: '32px',
		height: '32px',
		borderRadius: token('radius.xsmall'),
		alignItems: 'center',
		justifyContent: 'center',
	},
	dropdownTriggerInnerStyles: {
		gap: token('space.050'),
	},
	dropdownTriggerTextStyles: {
		display: 'inline-block',
		flex: '1 0 0',
		overflow: 'hidden',
		textAlign: 'left',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	iconContainerStyles: {
		flexShrink: 0,
	},
	selected: {
		color: token('color.text.selected'),
		backgroundColor: token('color.background.selected'),
		borderColor: token('color.border.accent.blue'),
	},
	fieldStyles: {
		width: '100%',
	},
});

const FieldWrapper = ({
	useInline,
	children,
}: {
	children: React.ReactNode;
	useInline?: boolean;
}) =>
	useInline ? (
		<Inline alignBlock="center" spread="space-between">
			{children}
		</Inline>
	) : (
		<Stack>{children}</Stack>
	);

export type FieldOption<TOptionValue> = {
	description?: MessageDescriptor;
	/**
	 * Optional CSS `font-family` stack to render this option's label in. Used by
	 * the typography font pickers so each option previews in its own typeface.
	 * Applies to both the open menu item and the selected value in the trigger.
	 * Omitted for all other fields, which render in the default UI font.
	 */
	fontFamily?: string;
	icon?: React.ElementType;
	label: MessageDescriptor;
	value: TOptionValue;
};

type FieldDropdownProps<TOptionValue> = {
	id: string;
	isDisabled: boolean;
	onSelect: (option: FieldOption<TOptionValue>) => void;
	options: FieldOption<TOptionValue>[];
	selectedOption: FieldOption<TOptionValue>;
	showUpdatedStyles: boolean;
};

const FieldDropdown = <TOptionValue,>({
	id,
	isDisabled,
	onSelect,
	selectedOption,
	showUpdatedStyles,
	options,
}: FieldDropdownProps<TOptionValue>) => (
	<DropdownMenu
		trigger={({ triggerRef, ...props }) => (
			<Pressable
				{...props}
				id={id}
				isDisabled={isDisabled}
				ref={triggerRef}
				xcss={cx(
					styles.dropdownTrigger,
					styles.border,
					!showUpdatedStyles && styles.dropdownFullWidthStyles,
					props.isSelected && styles.selected,
				)}
			>
				<Inline
					alignBlock="center"
					xcss={cx(styles.dropdownTriggerInnerStyles, props.isSelected && styles.selected)}
				>
					<Inline xcss={styles.dropdownTriggerTextStyles}>
						<Text color="inherit" weight="medium">
							<OptionLabel label={selectedOption.label} fontFamily={selectedOption.fontFamily} />
						</Text>
					</Inline>
					<Box xcss={styles.iconContainerStyles}>
						<ChevronDownIcon spacing="none" label="" size="small" />
					</Box>
				</Inline>
			</Pressable>
		)}
		shouldFitContainer={!showUpdatedStyles}
	>
		<DropdownItemGroup>
			{options.map((option) => {
				return (
					<DropdownItem
						isSelected={selectedOption.value === option.value}
						key={`${option.value}-key`}
						{...(option.icon && {
							elemBefore: (
								<Flex xcss={styles.iconBackgroundStyles}>
									<option.icon label={`${option.value}-option-icon`} color={token('color.icon')} />
								</Flex>
							),
						})}
						{...(option.description && {
							description: <FormattedMessage {...option.description} />,
						})}
						onClick={() => onSelect(option)}
					>
						<OptionLabel label={option.label} fontFamily={option.fontFamily} />
					</DropdownItem>
				);
			})}
		</DropdownItemGroup>
	</DropdownMenu>
);

type ConfigFieldDropdownProps<TOptionValue> = PropsWithChildren<{
	fieldType: string;
	infoTooltip?: string;
	label: MessageDescriptor;
	onSelect: (option: FieldOption<TOptionValue>) => void;
	options: FieldOption<TOptionValue>[];
	renderVariant: RenderVariant;
	selectedOption: FieldOption<TOptionValue>;
	showUpdatedStyles?: boolean;
	source: string;
}>;

export const ConfigFieldDropdown = <TOptionValue,>({
	children,
	fieldType,
	label,
	onSelect,
	options,
	renderVariant,
	selectedOption,
	showUpdatedStyles = false,
	infoTooltip,
	source,
}: ConfigFieldDropdownProps<TOptionValue>): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isDisabled = useIsDisabledContext();

	const handleSelect = useCallback(
		(selectedLabel: FieldOption<TOptionValue>) => {
			onSelect(selectedLabel);

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'dropdownItem',
					actionSubjectId: `${fieldType}Dropdown`,
					source,
					attributes: {
						renderVariant,
						selectedValue: selectedLabel.value,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, fieldType, onSelect, renderVariant, source],
	);

	return (
		<Flex direction="column" xcss={cx(styles.fieldStyles)}>
			<Stack space="space.200">
				<FieldWrapper useInline={showUpdatedStyles}>
					<Inline space="space.100" alignBlock="center">
						<Label htmlFor={`${fieldType}-dropdown`}>
							<Text
								size={showUpdatedStyles ? 'medium' : 'small'}
								color={showUpdatedStyles ? 'color.text' : 'color.text.subtlest'}
								weight={showUpdatedStyles ? 'regular' : 'bold'}
							>
								<FormattedMessage {...label} />
							</Text>
						</Label>
						{infoTooltip && (
							<Tooltip content={infoTooltip} position="bottom">
								<StatusInformationIcon
									testId="info-icon"
									label={infoTooltip}
									color={token('color.icon.discovery')}
								/>
							</Tooltip>
						)}
					</Inline>
					<FieldDropdown
						id={`${fieldType}-dropdown`}
						isDisabled={isDisabled}
						onSelect={handleSelect}
						selectedOption={selectedOption}
						showUpdatedStyles={showUpdatedStyles}
						options={options}
					/>
				</FieldWrapper>
			</Stack>
			{children}
		</Flex>
	);
};
